import { getAPIBaseURL } from './helpers'
import { getData, postData } from './request'

const base = () => `${getAPIBaseURL()}/v1`

/**
 * Initialize payment for an order
 * @param {Object} payload - Payment initialization data
 * @param {String} payload.orderId - Order ID
 * @param {String} payload.eventId - Event ID
 * @param {String} payload.redirectUrl - URL to redirect after payment
 * @returns {Promise<Object>} Payment initialization response with payment link
 */
export const initializePayment = async (payload) => {
  const url = `${base()}/payments/initialize`
  const token = localStorage.getItem('token')
  
  // Convert payload to URL-encoded format
  const formData = new URLSearchParams()
  if (payload.orderId) formData.append('orderId', payload.orderId)
  if (payload.eventId) formData.append('eventId', payload.eventId)
  if (payload.redirectUrl) formData.append('redirectUrl', payload.redirectUrl)
  
  const headers = { 
    'Content-Type': 'application/x-www-form-urlencoded',
    ...(token ? { 'token': token } : {})
  }
  const res = await postData(url, headers, formData.toString())
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  const errorData = res.result?.data
  const errorMessage = errorData?.data || errorData?.message || errorData || 'Failed to initialize payment'
  return { 
    status: res.result?.status || 500, 
    error: errorMessage 
  }
}

/**
 * Get user payments
 */
export const getUserPayments = async (params = {}) => {
  const url = `${base()}/payments`
  const token = localStorage.getItem('token')
  const headers = token ? { 'token': token } : {}
  const res = await getData(url, headers, params)
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  return { status: res.result?.status || 500, error: res.result?.data }
}

/**
 * Get payment by ID
 */
export const getPayment = async (paymentId) => {
  const url = `${base()}/payments/${paymentId}`
  const token = localStorage.getItem('token')
  const headers = token ? { 'token': token } : {}
  const res = await getData(url, headers, {})
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  return { status: res.result?.status || 500, error: res.result?.data }
}

/**
 * Verify payment after redirect from payment gateway
 */
export const verifyPayment = async (payload) => {
  const url = `${base()}/payments/verify`
  const token = localStorage.getItem('token')
  
  // Convert payload to URL-encoded format
  const formData = new URLSearchParams()
  if (payload.orderId) formData.append('orderId', payload.orderId)
  if (payload.txRef) formData.append('txRef', payload.txRef)
  if (payload.transactionId) formData.append('transactionId', payload.transactionId)
  
  const headers = { 
    'Content-Type': 'application/x-www-form-urlencoded',
    ...(token ? { 'token': token } : {})
  }
  const res = await postData(url, headers, formData.toString())
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  const errorData = res.result?.data
  const errorMessage = errorData?.data || errorData?.message || errorData || 'Failed to verify payment'
  return { 
    status: res.result?.status || 500, 
    error: errorMessage 
  }
}

/**
 * List all payments for admin
 */
export const listAdminPayments = async (params = {}) => {
  const url = `${base()}/admin/payments`
  const token = localStorage.getItem('token')
  const headers = token ? { 'token': token } : {}
  const res = await getData(url, headers, params)
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  return { status: res.result?.status || 500, error: res.result?.data }
}

/**
 * Get payment by ID for admin
 */
export const getAdminPayment = async (paymentId) => {
  const url = `${base()}/admin/payments/${paymentId}`
  const token = localStorage.getItem('token')
  const headers = token ? { 'token': token } : {}
  const res = await getData(url, headers, {})
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data?.data || res.result.data }
  }
  return { status: res.result?.status || 500, error: res.result?.data }
}

